import React, { useEffect, useState } from "react";
import axios from "axios";
import { Inbox, MapPin, Flag, DollarSign, Gauge, Package, CalendarClock, RefreshCw, AlertTriangle } from "lucide-react";
import { apiBaseUrl, authHeaders, isAdmin, toUtcDate } from "./shared";

// Inbound TMS load queue. New loads pulled from the client's TMS land here as status='queued' for a
// dispatcher to pick up (assign a truck + route). Read-only for now -- the assign action reuses the
// existing Operational Center flow and comes in a later phase. Scoped server-side/ACL to the caller;
// a plain dispatcher additionally filters to their own loads (loads.dispatcher_id) client-side, same
// convention LoadsPage uses, so one dispatcher's queue never shows another's.

function fmtMoney(v) {
  if (v == null || isNaN(v)) return "—";
  return "$" + Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtDate(iso) {
  const d = toUtcDate(iso);
  if (!d) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function rpm(rate, miles) {
  if (!rate || !miles) return null;
  return rate / miles;
}

export default function LoadQueue() {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const conditions = [{ field: "status", operator: "is", value: "queued" }];
      if (!isAdmin()) {
        const me = localStorage.getItem("id");
        if (me) conditions.push({ field: "dispatcher_id", operator: "is", value: me });
      }
      const url = `${apiBaseUrl}/search/loads/?filter_json=${encodeURIComponent(
        JSON.stringify({ conditions })
      )}&limit=500`;
      const res = await axios.get(url, authHeaders());
      // /search/{table}/ returns { total, records, qualifier_active }
      const rows = Array.isArray(res.data) ? res.data : res.data?.records || res.data?.results || res.data?.items || [];
      // newest first by pickup, then created
      rows.sort((a, b) => (b.pickup_datetime || "").localeCompare(a.pickup_datetime || ""));
      setLoads(rows);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load the queue.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh each minute; the pull job fills this server-side
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Inbox className="w-6 h-6 text-violet-600" />
          <h1 className="text-2xl font-semibold text-slate-800">Load Queue</h1>
          {!loading && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-violet-100 text-violet-700 text-sm font-semibold">
              {loads.length}
            </span>
          )}
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        New loads from your TMS, waiting to be assigned a truck. Open one in the Operational Center to plan and dispatch it.
      </p>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm py-16 text-center">Loading queue…</div>
      ) : loads.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-200 rounded-xl bg-slate-50/60">
          <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500 font-medium">Your queue is empty</div>
          <div className="text-slate-400 text-sm mt-1">New TMS loads will appear here as they arrive.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {loads.map((l) => {
            const r = rpm(l.rate_offered, l.miles_loaded);
            return (
              <div
                key={l.id}
                className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-violet-700 bg-violet-100 rounded-full px-2 py-0.5">
                        Queued
                      </span>
                      <span className="text-sm font-semibold text-slate-800 truncate">
                        {l.customer_name || "Unknown customer"}
                      </span>
                      {l.external_load_id && (
                        <span className="text-xs text-slate-400">#{l.external_load_id}</span>
                      )}
                    </div>
                    <div className="mt-2 flex items-start gap-2 text-sm text-slate-700">
                      <MapPin className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span className="truncate">{l.origin_location || "—"}</span>
                    </div>
                    <div className="mt-1 flex items-start gap-2 text-sm text-slate-700">
                      <Flag className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
                      <span className="truncate">{l.destination_location || "—"}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{fmtMoney(l.rate_offered)}</div>
                    {r != null && (
                      <div className="text-xs text-slate-500 tabular-nums">${r.toFixed(2)}/mi</div>
                    )}
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1">
                    <Gauge className="w-3.5 h-3.5 text-slate-400" />
                    {l.miles_loaded ? `${Math.round(l.miles_loaded)} mi` : "— mi"}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Package className="w-3.5 h-3.5 text-slate-400" />
                    {l.required_equipment_type_id ? "Equipment set" : "Any equipment"}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CalendarClock className="w-3.5 h-3.5 text-slate-400" />
                    Pickup {fmtDate(l.pickup_datetime)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CalendarClock className="w-3.5 h-3.5 text-slate-400" />
                    Delivery {fmtDate(l.delivery_datetime)}
                  </span>
                  {l.hazmat_status === "hazardous" && (
                    <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                      <AlertTriangle className="w-3.5 h-3.5" /> Hazmat
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
